import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, TrendingUp, Users, DollarSign, Plus, X, MapPin, Calendar } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DeveloperPanelPage = () => {
  const [projects, setProjects] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newProject, setNewProject] = useState({
    name: '',
    location: '',
    total_units: 100,
    starting_price: 5000000,
    completion_date: '2026-12-31',
    commission_percent: 3.0,
    description: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/developer/projects`),
        axios.get(`${API}/developer/analytics`)
      ]);
      setProjects(projectsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      toast.error('Failed to load developer data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    try {
      await axios.post(`${API}/developer/projects`, newProject);
      toast.success('Project created successfully!');
      setShowAddModal(false);
      setNewProject({
        name: '',
        location: '',
        total_units: 100,
        starting_price: 5000000,
        completion_date: '2026-12-31',
        commission_percent: 3.0,
        description: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create project');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED', minimumFractionDigits: 0 }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-gold text-xl">Loading developer panel...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian text-text-primary">
      <nav className="glass-card border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-[1440px] mx-auto px-8 py-6 flex justify-between items-center">
          <Link to="/dashboard" className="font-playfair text-3xl font-semibold text-gold">ASTIIV BASGAT</Link>
          <div className="flex items-center gap-6">
            <Link to="/dashboard" className="text-text-secondary hover:text-gold transition-colors">Dashboard</Link>
            <Link to="/analytics" className="text-text-secondary hover:text-gold transition-colors">Analytics</Link>
            <Link to="/investor" className="text-text-secondary hover:text-gold transition-colors">Investor</Link>
            <Link to="/developer" className="text-gold">Developer</Link>
            <Link to="/agent" className="text-text-secondary hover:text-gold transition-colors">Agent</Link>
          </div>
        </div>
      </nav>

      <div className="max-w-[1440px] mx-auto px-8 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex justify-between items-start mb-12">
          <div>
            <h1 className="font-playfair text-5xl font-semibold mb-4">Developer Panel</h1>
            <p className="text-text-secondary text-lg">Manage your projects, track leads, and monitor performance</p>
          </div>
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-gold text-black hover:bg-gold-light"
            data-testid="add-project-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Project
          </Button>
        </motion.div>

        {/* Analytics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-8" data-testid="active-projects-card">
            <Building2 className="w-8 h-8 text-gold mb-4" />
            <div className="text-3xl font-bold mb-2">{analytics?.active_projects || 0}</div>
            <div className="text-text-secondary">Active Projects</div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="glass-card p-8" data-testid="total-leads-card">
            <Users className="w-8 h-8 text-gold mb-4" />
            <div className="text-3xl font-bold mb-2">{analytics?.total_leads || 0}</div>
            <div className="text-text-secondary">Total Leads</div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="glass-card p-8" data-testid="occupancy-card">
            <TrendingUp className="w-8 h-8 text-gold mb-4" />
            <div className="text-3xl font-bold mb-2">{analytics?.occupancy_rate || 0}%</div>
            <div className="text-text-secondary">Occupancy Rate</div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="glass-card p-8" data-testid="total-units-card">
            <DollarSign className="w-8 h-8 text-gold mb-4" />
            <div className="text-3xl font-bold mb-2">{analytics?.sold_units || 0}/{analytics?.total_units || 0}</div>
            <div className="text-text-secondary">Units Sold</div>
          </motion.div>
        </div>

        {/* Projects List */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <h2 className="font-playfair text-3xl font-semibold mb-6">Your Projects</h2>
          
          {projects.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <Building2 className="w-16 h-16 text-gold mx-auto mb-6 opacity-50" />
              <h3 className="font-playfair text-2xl font-semibold mb-4">No Projects Yet</h3>
              <p className="text-text-secondary text-lg mb-6">Start by adding your first development project</p>
              <Button onClick={() => setShowAddModal(true)} className="bg-gold text-black hover:bg-gold-light">
                <Plus className="w-4 h-4 mr-2" /> Add Your First Project
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="glass-card p-6 hover:border-gold/30 transition-all"
                  data-testid={`project-card-${index}`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="font-playfair text-xl font-semibold">{project.name}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                      project.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {project.status}
                    </span>
                  </div>
                  
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-2 text-text-secondary">
                      <MapPin className="w-4 h-4" />
                      {project.location}
                    </div>
                    <div className="flex items-center gap-2 text-text-secondary">
                      <Calendar className="w-4 h-4" />
                      Completion: {project.completion_date}
                    </div>
                    <div className="flex justify-between items-center pt-4 border-t border-white/10">
                      <span className="text-text-secondary">Units</span>
                      <span className="text-gold font-bold">{project.sold_units || 0}/{project.total_units}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-text-secondary">Starting</span>
                      <span className="text-text-primary font-semibold">{formatCurrency(project.starting_price)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-text-secondary">Commission</span>
                      <span className="text-emerald-400 font-bold">{project.commission_percent}%</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Add Project Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-card p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-playfair text-2xl font-semibold">Add New Project</h2>
              <Button variant="ghost" onClick={() => setShowAddModal(false)} className="text-text-secondary hover:text-white">
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-text-primary mb-2 block">Project Name</Label>
                <Input
                  value={newProject.name}
                  onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                  className="bg-white text-black border-white/20 focus:border-gold"
                  placeholder="e.g., Palm Residences"
                />
              </div>

              <div>
                <Label className="text-text-primary mb-2 block">Location</Label>
                <Input
                  value={newProject.location}
                  onChange={(e) => setNewProject({ ...newProject, location: e.target.value })}
                  className="bg-white text-black border-white/20 focus:border-gold"
                  placeholder="e.g., Dubai Marina"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-text-primary mb-2 block">Total Units</Label>
                  <Input
                    type="number"
                    value={newProject.total_units}
                    onChange={(e) => setNewProject({ ...newProject, total_units: parseInt(e.target.value) })}
                    className="bg-white text-black border-white/20 focus:border-gold"
                  />
                </div>
                <div>
                  <Label className="text-text-primary mb-2 block">Starting Price (AED)</Label>
                  <Input
                    type="number"
                    value={newProject.starting_price}
                    onChange={(e) => setNewProject({ ...newProject, starting_price: parseFloat(e.target.value) })}
                    className="bg-white text-black border-white/20 focus:border-gold"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-text-primary mb-2 block">Completion Date</Label>
                  <Input
                    type="date"
                    value={newProject.completion_date}
                    onChange={(e) => setNewProject({ ...newProject, completion_date: e.target.value })}
                    className="bg-white text-black border-white/20 focus:border-gold"
                  />
                </div>
                <div>
                  <Label className="text-text-primary mb-2 block">Commission (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newProject.commission_percent}
                    onChange={(e) => setNewProject({ ...newProject, commission_percent: parseFloat(e.target.value) })}
                    className="bg-white text-black border-white/20 focus:border-gold"
                  />
                </div>
              </div>

              <div>
                <Label className="text-text-primary mb-2 block">Description</Label>
                <textarea
                  value={newProject.description}
                  onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                  className="w-full bg-white text-black border border-white/20 focus:border-gold rounded-md p-3 min-h-[100px]"
                  placeholder="Project description..."
                />
              </div>

              <Button
                onClick={handleCreateProject}
                disabled={!newProject.name || !newProject.location}
                className="w-full bg-gold text-black hover:bg-gold-light mt-4"
                data-testid="submit-project-btn"
              >
                Create Project
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default DeveloperPanelPage;
